// Re-broadcasts the local user's direct `ping.profile` intro into a 1:1
// conversation, ONCE per conversation per app session.
//
// Why: NewChatModal sends the inviter's profile intro as the first application
// message right after the MLS Commit — but that's exactly when the receiver
// may not have processed the Welcome yet, so the SDK can't decrypt it and it's
// dropped. The receiver renames its 'New chat' row whenever the profile DOES
// arrive (`useIncomingMessages` ping.profile `direct` branch), so re-sending
// the intro from the normal send path — long after the peer has joined —
// reliably resolves the peer name. Idempotent on the receiver (only fills the
// 'New chat' placeholder), and ignored when it describes our own account.

import { log } from '@core/log/log';
import { db } from '@core/persistence/db';
import { retrieveIdentity } from '@core/persistence/identity';

interface ConvLike {
  send(bytes: Uint8Array): Promise<unknown>;
}

const sentThisSession = new Set<string>();

/**
 * Clear the once-per-session cache so the NEXT send in each conversation
 * re-broadcasts our direct profile. Called when a SIBLING device links
 * (`device_added`): a device that joined our conversations AFTER our last
 * profile broadcast never received our identity, so it can't map our sender
 * DEVICE → ACCOUNT and renders our messages as the peer. Re-broadcasting lets
 * it recognise our messages as self.
 */
export function resetDirectProfileIntroSession(): void {
  sentThisSession.clear();
}

export async function resendDirectProfileIntro(
  conv: ConvLike,
  conversationId: string,
): Promise<void> {
  if (sentThisSession.has(conversationId)) return;
  try {
    const row = await db.conversations.get(conversationId);
    // Only 1:1 chats carry a single peer to name; groups derive names from the
    // roster + group meta instead.
    if (!row || row.kind !== 'direct') return;
    const identity = await retrieveIdentity();
    if (!identity?.accountId) return;

    // Mark before the send so a concurrent send for the same conversation
    // doesn't double-broadcast; rolled back on failure so a later send retries.
    sentThisSession.add(conversationId);
    const profile = {
      kind: 'ping.profile',
      payload: {
        accountId: identity.accountId,
        displayName:
          identity.displayName && identity.displayName.length > 0
            ? identity.displayName
            : 'Someone',
        // 1:1 marker — receiver names the row after us (the other party).
        direct: true,
      },
    };
    await conv.send(new TextEncoder().encode(JSON.stringify(profile)));
    log.info({
      event: 'chat.profileIntro.resent',
      conversationIdPrefix: conversationId.slice(0, 8),
    });
  } catch (err) {
    sentThisSession.delete(conversationId);
    log.debug({
      event: 'chat.profileIntro.resend.failed',
      error: (err as Error).message,
    });
  }
}
